import React from 'react'
import { Modal, ModalBody, Row, Col, Button } from 'reactstrap'
import logoImage from "assets/images/eventox_logo/EVENTOX_LOGO.png"
import 'assets/scss/custom/components/_payout-receipt.scss'

const currencySymbols = {
  USD: '$',
  AUD: '$',
  NZD: '$',
  CAD: '$',
  SGD: '$',
  HKD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  INR: '₹',
  AED: 'د.إ',
  SAR: 'ر.س',
  QAR: 'ر.ق',
}

const getSymbol = (code) => currencySymbols[code?.toUpperCase()] || '$'

const PayoutReceiptModal = ({ isOpen, toggle, data }) => {
  if (!data) return null

  const handlePrint = () => {
    const originalTitle = document.title
    const eventName = data.organizer.event || "Event"
    document.title = `${eventName.replace(/[^\w\s-]/gi, '').trim()} - Payout Receipt`

    const styleEl = document.createElement('style')
    styleEl.id = '__receipt-page-size__'
    styleEl.innerHTML = `@media print { @page { size: A4 !important; margin: 0 !important; } }`
    document.head.appendChild(styleEl)

    setTimeout(() => {
      window.print()
      setTimeout(() => {
        document.title = originalTitle
        document.getElementById('__receipt-page-size__')?.remove()
      }, 1000)
    }, 150)
  }

  const currencyCode = data.receipt.currency || "USD"

  const formatCurrency = (amount) =>
    `${getSymbol(currencyCode)}${Math.abs(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const grossSales = data.breakdown.find(i => i.type === 'income')?.amount || 0
  const totalDeductions = data.breakdown.filter(i => i.type === 'fee').reduce((sum, i) => sum + Math.abs(i.amount), 0)

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered className="payout-receipt-modal" style={{ maxWidth: 850 }}>
      <ModalBody className="p-0">
        <button type="button" className="btn-close receipt-close d-print-none" onClick={toggle} aria-label="Close" />

        <div className="receipt-container">

          {/* ── Header ── */}
          <div className="receipt-header border-bottom">
            <div className="receipt-header__glow" />
            <div className="receipt-header__inner header-flex-row">
              <div>
                <h1 className="receipt-title">PAYOUT <span className="receipt-title--accent">RECEIPT</span></h1>
                {/* <p className="receipt-ref">Ref No: {data.receipt.number}</p>
                <span className="receipt-badge">
                  <span className="receipt-badge__dot" />
                  {data.receipt.status}
                </span> */}
              </div>
              <div className="receipt-brand">
                <img src={logoImage} alt="EVENTOX LOGO" className="receipt-brand__logo" />
                <p className="receipt-brand__tagline">PLAN YOUR DAY</p>
              </div>
            </div>
          </div>

          {/* ── Net Settlement Banner ── */}
          <div className="receipt-banner border-bottom">
            <div>
              <div className="receipt-banner__label-row">
                <span className="receipt-banner__dot" />
                <p className="receipt-banner__label">Net Settlement Amount</p>
              </div>
              <div className="receipt-banner__amount-row">
                <h2 className={`receipt-amount ${data.netPayout.amount < 0 ? 'receipt-amount--negative' : ''}`}>
                  {formatCurrency(data.netPayout.amount)}
                </h2>
                <span className="receipt-amount__currency">{currencyCode}</span>
              </div>
            </div>
            <div className="receipt-banner__stats">
              <div className="text-end">
                <p className="receipt-stat__label">Gross Sales</p>
                <p className="receipt-stat__value">{formatCurrency(grossSales)}</p>
              </div>
              <div className="text-end">
                <p className="receipt-stat__label">Total Deductions</p>
                <p className="receipt-stat__value receipt-stat__value--negative">-{formatCurrency(totalDeductions)}</p>
              </div>
            </div>
          </div>

          {/* ── Body ── */}
          <div className="receipt-body">

            {/* Section 1: Analytics & Identity */}
            <Row className="g-4">
              <Col xs={12} md={6}>
                <div className="receipt-section-header">
                  <i className="bx bx-receipt receipt-section-header__icon" />
                  <h3 className="receipt-section-header__title">Receipt Analytics</h3>
                </div>
                <Row className="g-3">
                  {[
                    { label: 'Date Issued',       value: data.receipt.issued    },
                    { label: 'Requested On',      value: data.receipt.requested },
                    { label: 'Payout Initiated',  value: data.receipt.initiated },
                    { label: 'Currency',          value: `${currencyCode} (${getSymbol(currencyCode)})`  },
                  ].map(({ label, value }) => (
                    <Col xs={6} key={label}>
                      <label className="receipt-field__label">{label}</label>
                      <p className="receipt-field__value">{value}</p>
                    </Col>
                  ))}
                </Row>
              </Col>

              <Col xs={12} md={6} className="ps-md-4">
                <div className="receipt-section-header">
                  <i className="bx bx-user receipt-section-header__icon" />
                  <h3 className="receipt-section-header__title">Organizer Identity</h3>
                </div>
                <div className="receipt-identity">
                  <div>
                    <label className="receipt-field__label">Organizer Name</label>
                    <p className="receipt-field__value receipt-field__value--strong">{data.organizer.name}</p>
                  </div>
                  <div>
                    <label className="receipt-field__label">Event Property</label>
                    <p className="receipt-field__value">{data.organizer.event}</p>
                  </div>
                </div>
              </Col>
            </Row>

            {/* Section 2: Breakdown Table */}
            <div>
              <div className="receipt-table-header">
                <div className="d-flex align-items-center gap-2">
                  <i className="bx bx-transfer receipt-section-header__icon" />
                  <h3 className="receipt-section-header__title mb-0">Payout Breakdown</h3>
                </div>
                <div className="d-print-none d-flex gap-2">
                  <Button color="light" size="sm" onClick={handlePrint} className="receipt-btn-icon">
                    <i className="bx bx-printer" />
                  </Button>
                  <Button size="sm" onClick={handlePrint} className="receipt-btn-export">
                    <i className="bx bxs-download" /> EXPORT .PDF
                  </Button>
                </div>
              </div>

              <div className="receipt-table-wrap">
                <table className="receipt-table w-100">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th className="text-end">Amount ({currencyCode})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.breakdown.map((item, idx) => {
                      const isNeg = item.amount < 0 || item.type === 'fee'
                      const abs   = Math.abs(item.amount)
                      return (
                        <tr key={idx}>
                          <td>{item.label}</td>
                          <td className={`text-end mono ${abs === 0 ? 'amount--zero' : isNeg ? 'amount--neg' : 'amount--pos'}`}>
                            {abs === 0 ? `${getSymbol(currencyCode)}0.00` : (isNeg ? '-' : '') + formatCurrency(abs)}
                          </td>
                        </tr>
                      )
                    })}
                    <tr className="receipt-table__total">
                      <td>Total Settlement Balance</td>
                      <td className="text-end mono">{formatCurrency(data.netPayout.amount)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

          </div>

          {/* ── Footer ── */}
          <div className="receipt-footer">
            <Row className="g-4">
              <Col xs={12} md={7}>
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bx bx-info-circle receipt-section-header__icon" />
                  <h3 className="receipt-footer__section-title">Important Disclosures</h3>
                </div>
                <ul className="receipt-disclosures">
                  {[
                    'Funds include cleared transactions up to payout initiation.',
                    'Pending transactions roll forward to the subsequent cycle.',
                    'System-generated authentic document; no signature required.',
                  ].map((text, i) => (
                    <li key={i}>
                      <i className="bx bx-chevron-right receipt-section-header__icon" />
                      <span>{text}</span>
                    </li>
                  ))}
                </ul>
              </Col>
              <Col xs={12} md={5} className="d-flex flex-column align-items-md-end justify-content-between gap-4 text-md-end">
                <div>
                  <h3 className="receipt-footer__company">Vibra Verse Ltd <span>NZ</span></h3>
                  <p className="receipt-footer__contact mono">hello@eventox.co</p>
                  <p className="receipt-footer__contact mono">www.eventox.co</p>
                </div>
                <div className="receipt-footer__badge">SECURE • TRANSPARENT • RELIABLE</div>
              </Col>
            </Row>

            <hr className="receipt-footer__divider" />

            <div className="receipt-footer__bottom">
              <span>Generated by Eventox Intelligence</span>
              <span>© {new Date().getFullYear()} EVENTOX PAYOUT SYSTEM</span>
            </div>
          </div>

        </div>
      </ModalBody>
    </Modal>
  )
}

export default PayoutReceiptModal